import { cn } from '@/shared/lib/cn';
import { formatRate } from '@/shared/lib/format-money';
import type { CommercialFxRate } from './api';
import { tickerItemTitle, usableRatesByCode } from './rates';
import { useCommercialFxRates } from './useCommercialFxRates';
import { useFxRates } from './useFxRates';

// Order matches the design reference: at narrower widths currencies drop off the
// end of the list one tier at a time.
const TICKER_ORDER = ['USD', 'EUR', 'KRW', 'CNY', 'JPY'];

// Visibility per position, by CSS breakpoint only (no resize listener, no re-render):
// 1st always; 2nd–3rd from 1150px; 4th from 1300px; 5th from 1560px.
const TIER_CLASSES = [
  'flex',
  'hidden min-[1150px]:flex',
  'hidden min-[1150px]:flex',
  'hidden min-[1300px]:flex',
  'hidden min-[1560px]:flex',
];

/**
 * The company's commercial rates in the header, the ones its quotes use (computed by the
 * server). Without them (no organization, or the request failed) it shows the plain CBR
 * rates, labelled as such. While the commercial rates load it shows nothing, so CBR rates
 * never appear under the commercial label.
 */
export function FxTicker() {
  const commercial = useCommercialFxRates();
  const { data: cbr } = useFxRates();

  let basis: 'commercial' | 'cbr';
  let entries: readonly CommercialFxRate[];
  if (commercial.data) {
    basis = commercial.data.basis;
    entries = commercial.data.rates;
  } else if (commercial.isError && cbr) {
    basis = 'cbr';
    entries = cbr.map((r) => ({ ...r, source: 'cbr' as const, markup_pct: null }));
  } else {
    return null;
  }

  const byCode = usableRatesByCode(entries);
  const items = TICKER_ORDER.flatMap((code) => {
    const rate = byCode.get(code);
    return rate ? [rate] : [];
  });

  if (items.length === 0) return null;
  const label = basis === 'commercial' ? 'Коммерческий курс' : 'ЦБ РФ';

  return (
    <div
      role="group"
      aria-label={basis === 'commercial' ? 'Коммерческий курс' : 'Курсы ЦБ РФ'}
      className="hidden shrink-0 items-center gap-3 md:flex"
    >
      <span className="text-[11px] font-medium uppercase tracking-wide text-outline-faint">
        {label}
      </span>
      {items.map((rate, i) => (
        <span
          key={rate.code}
          data-fx-code={rate.code}
          title={tickerItemTitle(rate)}
          className={cn(
            'items-center gap-1 text-[12.5px] font-medium text-outline-faint',
            TIER_CLASSES[i],
          )}
        >
          {rate.code}
          <span className="text-foreground">{formatRate(rate.rate_to_rub)} ₽</span>
        </span>
      ))}
    </div>
  );
}
